import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getTransactionNotes } from "@/lib/demo-data/transaction-notes";
import type { TransactionNote, TransactionNoteApiItem, TransactionNotesApiResponse, CreateTransactionNoteRequest } from "@/types/transaction-note";
import { useQueryClient } from "@tanstack/react-query";

function mapApiItemToTransactionNote(item: TransactionNoteApiItem): TransactionNote {
  return {
    id: String(item.id),
    transactionId: String(item.transactionId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch transaction notes
 * Handles both Demo and Live modes
 */
export function useTransactionNotes(transactionId: number | undefined) {
  const { apiGet } = useApi();
  const mockNotes = transactionId ? getTransactionNotes(String(transactionId)) : [];

  return useModeAwareQuery<TransactionNote[], Error>(
    ["transaction-notes", transactionId],
    async ({ signal }) => {
      if (!transactionId) return [];
      const response = await apiGet<TransactionNotesApiResponse>(
        `/api/v1/transactions/${transactionId}/notes`,
        undefined,
        signal
      );
      return (response.notes || []).map(mapApiItemToTransactionNote);
    },
    mockNotes,
    {
      enabled: !!transactionId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to add a new transaction note
 * Handles both Demo and Live modes
 */
export function useAddTransactionNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TransactionNote, Error, CreateTransactionNoteRequest & { userId: string; userName: string }>(
    async (request) => {
      const response = await apiPost<TransactionNoteApiItem>(
        `/api/v1/transactions/${request.transactionId}/notes`,
        {
          content: request.content,
        }
      );
      return mapApiItemToTransactionNote(response);
    },
    async (request) => {
      return {
        id: String(Date.now()),
        transactionId: String(request.transactionId),
        content: request.content,
        createdAt: new Date().toISOString(),
        createdBy: {
          id: request.userId,
          name: request.userName,
        },
      };
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ["transaction-notes", variables.transactionId] });
        queryClient.invalidateQueries({ queryKey: ["transaction-recent-activities", variables.transactionId] });
      },
    }
  );
}
